import type { ComponentChildren } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { visibleHeight } from '../lib/viewport';
import { AppIcon } from './AppIcon';

export function QuestionSelect({ label, value, options: given, onChange, menuWidth, disabled, placeholder, empty = 'Nothing to choose yet.' }: { menuWidth?: number | 'trigger'; disabled?: boolean;
  /** Shown while value matches no option. */ placeholder?: string; /** Shown in the open list when there are no options. */ empty?: string; label: string; value: string; options: { value: string; label: string; icon?: ComponentChildren; group?: string;
    /** A heading within the group, such as a question listed with each way it can be used; menuLabel names the option beneath it. */
    subgroup?: string; menuLabel?: string }[]; onChange: (value: string) => void }) {
  const id = useId();
  // Grouped options are listed, and moved through with the arrow keys, group by group.
  const groups = given.some(option => option.group) ? [...new Set(given.map(option => option.group ?? ''))] : undefined;
  const options = groups ? groups.flatMap(group => given.filter(option => (option.group ?? '') === group)) : given;
  const TYPES = options.map(option => option.value);
  const TYPE_LABELS = Object.fromEntries(options.map(option => [option.value, option.label]));
  const MENU_LABELS = Object.fromEntries(options.map(option => [option.value, option.menuLabel ?? option.label]));
  const chosen = options.find(option => option.value === value);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number; width: number; maxHeight: number } | null>(null);
  const initialFocus = useRef(value);
  const search = useRef({ text: '', time: 0 });
  const close = (restoreFocus = true) => { setPosition(null); if (restoreFocus) trigger.current?.focus({ preventScroll: true }); };
  const focus = (type: string) => {
    const option = panel.current?.querySelector<HTMLButtonElement>(`[data-value="${type}"]`);
    option?.focus({ preventScroll: true });
    option?.scrollIntoView?.({ block: 'nearest' });
  };
  const show = () => {
    const box = trigger.current!.getBoundingClientRect();
    const width = Math.min(menuWidth === 'trigger' ? box.width : menuWidth ?? Math.max(box.width, options.every(option => option.icon) ? 112 : 220), window.innerWidth - 16);
    initialFocus.current = value;
    search.current = { text: '', time: 0 };
    setPosition({ left: Math.max(8, Math.min(menuWidth !== undefined ? box.left : box.right - width, window.innerWidth - width - 8)), top: Math.max(8, box.top),
      width, maxHeight: Math.min(400, visibleHeight() - 16) });
  };
  useLayoutEffect(() => {
    if (!position || !panel.current) return;
    const menu = panel.current;
    // With nothing chosen yet, the list opens at its first option; with no options, at its note saying so.
    const selected = menu.querySelector<HTMLElement>(`[data-value="${initialFocus.current}"]`) ?? menu.querySelector<HTMLElement>('[data-value]') ?? menu;
    const box = trigger.current!.getBoundingClientRect();
    const menuBox = menu.getBoundingClientRect();
    const optionBox = selected.getBoundingClientRect();
    const center = box.top + box.height / 2;
    const selectedCenter = optionBox.top - menuBox.top + menu.scrollTop + optionBox.height / 2;
    const fullHeight = Math.max(menu.scrollHeight + 2, menuBox.height);
    // Place the selected row over the control. The list is at most 400px tall, so a long one scrolls to bring that
    // row into its window; near the viewport edges the list stays on screen and the row lands as close as it can.
    const height = visibleHeight(), maxHeight = Math.min(400, height - 16, fullHeight), scrollRange = Math.max(0, fullHeight - maxHeight);
    let scroll = Math.max(0, Math.min(scrollRange, selectedCenter - maxHeight / 2));
    const top = Math.max(8, Math.min(center - (selectedCenter - scroll), height - maxHeight - 8));
    scroll = Math.max(0, Math.min(scrollRange, selectedCenter - (center - top)));
    menu.style.maxHeight = `${maxHeight}px`;
    menu.scrollTop = scroll;
    setPosition({ ...position, top, maxHeight });
    selected.focus({ preventScroll: true });
  }, [!!position]);
  useEffect(() => {
    if (!position) return;
    const outside = (event: Event) => {
      if (event.target instanceof Node && !panel.current?.contains(event.target) && !trigger.current?.contains(event.target)) close(false);
    };
    const scroll = (event: Event) => { if (!(event.target instanceof Node) || !panel.current?.contains(event.target)) close(); };
    const resize = () => close();
    document.addEventListener('pointerdown', outside);
    document.addEventListener('focusin', outside);
    window.addEventListener('scroll', scroll, true);
    window.addEventListener('resize', resize);
    return () => {
      document.removeEventListener('pointerdown', outside); document.removeEventListener('focusin', outside);
      window.removeEventListener('scroll', scroll, true); window.removeEventListener('resize', resize);
    };
  }, [!!position]);

  const renderOption = (option: typeof options[number]) => <button type="button" role="option" class={`question-type-option${option.subgroup ? ' question-select-suboption' : ''}`} key={option.value} data-value={option.value} tabIndex={-1} aria-selected={option.value === value} aria-label={option.menuLabel ?? option.label}
    onClick={() => { onChange(option.value); close(); }}>{option.icon ?? option.menuLabel ?? option.label}</button>;
  return <div class="validation-select">
    <button type="button" class="question-type-trigger validation-select-trigger" ref={trigger} aria-label={label} disabled={disabled}
      aria-haspopup="listbox" aria-describedby={`${id}-value`} aria-expanded={!!position} aria-controls={`${id}-types`}
      onClick={() => position ? close() : show()}
      onKeyDown={event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); if (position) focus(TYPE_LABELS[value] !== undefined ? value : TYPES[0]); else show(); }
      }}>
      {/* An option listed under a heading shows only its own name, such as one checkbox choice without its long question: the
        open list shows the heading. Its full name is still read out, and shown on hover. */}
      <span id={`${id}-value`} title={chosen?.menuLabel && chosen.label}>{chosen?.icon ? <>{chosen.icon}<span class="sr-only">{chosen.label}</span></>
        : chosen?.menuLabel ? <><span aria-hidden="true">{chosen.menuLabel}</span><span class="sr-only">{chosen.label}</span></>
        : chosen?.label ?? <span class="question-select-placeholder">{placeholder}</span>}</span><AppIcon name="chevron-down" />
    </button>
    {position && createPortal(<div class={`question-type-menu validation-select-menu${menuWidth !== undefined ? ' rating-select-menu' : ''}`} ref={panel} id={`${id}-types`} role="listbox" aria-label={label} style={position} tabIndex={-1}
      onKeyDown={event => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); return; }
        // Resume the form's normal tab order instead of tabbing through the portal.
        if (event.key === 'Tab') { close(); return; }
        const index = TYPES.indexOf((document.activeElement as HTMLElement)?.dataset.value as string);
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          focus(TYPES[event.key === 'Home' ? 0 : event.key === 'End' ? TYPES.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + TYPES.length) % TYPES.length]);
        } else if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault();
          const text = (Date.now() - search.current.time < 700 ? search.current.text : '') + event.key.toLowerCase();
          const query = [...text].every(char => char === text[0]) ? text[0] : text;
          search.current = { text, time: Date.now() };
          const next = [...TYPES.slice(index + 1), ...TYPES.slice(0, index + 1)].find(type => MENU_LABELS[type].toLowerCase().startsWith(query));
          if (next) focus(next);
        }
      }}>
      {/* Options that name a group are listed under its heading, in the order the groups first appear. */}
      {groups ? groups.map((group, g) => <div role="group" aria-labelledby={`${id}-group-${g}`} key={group}>
        <div class="question-select-group" id={`${id}-group-${g}`} role="presentation">{group}</div>
        {/* Within a group, a run of options sharing a subgroup sits under that subgroup's own heading. */}
        {runs(options.filter(option => (option.group ?? '') === group)).map((run, r) => run[0].subgroup
          ? <div role="group" aria-labelledby={`${id}-group-${g}-${r}`} key={`${run[0].subgroup}-${r}`} class="question-select-subgroup">
            <div class="question-select-subgroup-heading" id={`${id}-group-${g}-${r}`} role="presentation">{run[0].subgroup}</div>
            {run.map(renderOption)}
          </div>
          : run.map(renderOption))}
      </div>) : options.length ? options.map(renderOption) : <p class="question-select-empty">{empty}</p>}
    </div>, trigger.current?.closest('dialog') ?? document.body)}
  </div>;
}

/** Consecutive options that share a subgroup, or a lone option with none. */
function runs<T extends { subgroup?: string }>(options: T[]): T[][] {
  const out: T[][] = [];
  for (const option of options) {
    const last = out.at(-1);
    if (last && option.subgroup && last[0].subgroup === option.subgroup) last.push(option); else out.push([option]);
  }
  return out;
}
